import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SelectableMode, SelectableSettings, SelectionEvent } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { GroupGeneralApprovalData } from '../../../raci/models/group-general-approval-data';
import { UserDashboardService } from '../../services/user-dahsboard.service';
import { EventBusService } from '../../../../services/event-bus.service';

/**
 * Handles the general tasks approval matrix component
 */
@Component({
  selector: 'general-tasks-approval-matrix',
  templateUrl: './general-tasks-approval-matrix.component.html',
  styleUrls: ['./general-tasks-approval-matrix.component.css']
})
export class GeneralTasksApprovalMatrixComponent implements OnInit {
  /**
 * The current user.
 */
  @Input() currentUser: any;

  /**
   * The grouping criteria.
   */
  @Input() groupBy: any;

  /**
   * Setter for the general task approval matrix.
   */
  @Input() set generalTaskApprovalMatrix(value: any) {
    if (value) {
      this.selectedApprovals = [];
      this.isProjectSelected = false;
      this.data = value;
      this.properties = value?.capForceProject?.Properties;
      this.properties.forEach(item => item.isSelected = false);
    }
  }

  /**
   * Event emitter for updating the user dashboard complete task status.
   */
  @Output() updateUserDashboardCompleteTaskStatus: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Event emitter for refreshing the user task list.
   */
  @Output() refreshUserTaskList: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Event emitter for updating the selected general task property.
   */
  @Output() updateSelectedGeneralTaskProperty: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Array of properties grouped by sector.
   */
  propertiesBySector: Array<GroupGeneralApprovalData> = [];

  /**
   * Array of properties grouped by region.
   */
  propertiesByRegion: Array<GroupGeneralApprovalData> = [];

  /**
   * Array of properties.
   */
  properties: any[] = [];

  /**
   * Array of group data.
   */
  groupData: any[] = [];

  /**
   * The approval comment.
   */
  approvalComment: any;

  /**
   * The data.
   */
  data: any;

  /**
   * The selectable mode.
   */
  mode: SelectableMode = "single";

  /**
   * Indicates whether dragging is enabled.
   */
  drag = false;

  /**
   * The selectable settings.
   */
  selectableSettings: SelectableSettings;

  /**
   * The selected property.
   */
  selectedProperty: any;

  /**
   * Array of selected approvals.
   */
  selectedApprovals: Array<any> = [];

  /**
   * Indicates whether a project is selected.
   */
  isProjectSelected = false;

  /**
   * Indicates whether the approval dialog is displayed.
   */
  isApprovalDialogDisplayed = false;

  /**
   * Indicates whether the approve action is triggered.
   */
  isApproveTriggered = false;

  /**
  * Selected business unit from approval dialog
  */
  selectedBusinessUnit: number;

  /**
   * Selected regional group from approval dialog
   */
  selectedRegionalGroup: number;

  /**
   * Array of subscriptions.
   */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();

  /**
   * Constructor
   * @param _userDashboardService UserDashboardService
   * @param _raciNavigationService RaciNavigationService
   */
  constructor(private eventBus: EventBusService,
    private _userDashboardService: UserDashboardService,
    private _raciNavigationService: RaciNavigationService) {
    this.setSelectableSettings();
  }

  /**
   * On init
   * */

  ngOnInit() {
    this.showRaciApprovalDialog();
  }

  /**
   * On changes actions
   * */
  ngOnChanges() {
    if (this.properties) {
      this.groupProperties();
    }
  }

  /**
  * Set Selectable Settings
  */
  public setSelectableSettings(): void {
    this.selectableSettings = {
      mode: this.mode,
      drag: this.drag,
    };
  }

  /**
   * Group by properties
   * */
  groupProperties() {
    this.groupBySector();
    this.groupByRegion();
    this.groupData = this.groupBy == 'Sector' ? this.propertiesBySector : this.propertiesByRegion;
  }

  /**
   * Groups properties by sector
   * */
  private groupBySector() {
    this.propertiesBySector = [];
    this.properties.forEach(property => {
      if (!this.propertiesBySector.find(item => { return item.group == property.PropertyType })) {
        this.propertiesBySector.push(new GroupGeneralApprovalData({ "group": property.PropertyType }));
      }
      this.propertiesBySector.find(item => { return item.group == property.PropertyType }).properties.push(property);
    });
  }

  /**
   * Group properties by region
   * */
  private groupByRegion() {
    this.propertiesByRegion = [];
    this.properties.forEach(property => {
      if (!this.propertiesByRegion.find(item => { return item.group == property.PropertyRegionManual })) {
        this.propertiesByRegion.push(new GroupGeneralApprovalData({ "group": property.PropertyRegionManual }));
      }
      this.propertiesByRegion.find(item => { return item.group == property.PropertyRegionManual }).properties.push(property);
    });
  }

  /**
  * Opens modal for approval or rejection
  * */
  approveRejectModal() {
    if (!this.data?.dateApproved && !this.data?.dateRejected) {
      if (this.isApproveTriggered) {
        this.approve();
      } else {
        this.reject();
      }
    }
  }

  /**
   * Show or hide data loader
   * @param state State
   * */
  showHideDataLoader(state: boolean) {
    this.eventBus.broadcastEvent("dataLoader", {
      displayLoader: () => {
        return state;
      }
    });
  }

  /**
  * Handles on approve action
  */
  onApprove(event: any) {
    if (event) {
      this.approvalComment = event.comment;
      this.selectedRegionalGroup = event.regionalGroup ? event.regionalGroup : 0;
      this.selectedBusinessUnit = event.businessUnit;
      this.data.capForceProject.OwnerBuId = this.selectedBusinessUnit;
      this.data.regionId = this.selectedRegionalGroup;
      this._userDashboardService.displayDataLoader(true);
      this.isApproveTriggered = event.type === 'approve';
      this.approveRejectModal();
    }

  }

  /**
  * Give approval to selected approval
  * */
  approve() {
    this._userDashboardService.displayDataLoader(true);
    this.subscribeToApproveEvent();
  }

  /**
  * Reject approval task
  * */
  public reject() {
    this._userDashboardService.displayDataLoader(true);
    this.subscribeToRejectEvent(this.properties);
  }

  /**
   * Subscribes to approve event action on raci service
   */
  private subscribeToApproveEvent() {
    this._subscriptions.push(this._userDashboardService.approve(this.data, this.approvalComment).subscribe(response => {
      if (response) {
        this.data.isCompleted = true;
        this.data.dateApproved = Date.now();
        this.updateUserDashboardCompleteTaskStatus.emit(true);
        this.refreshUserTaskList.emit(true);

        this.closeApprovalMatrixDialog();
        this.eventBus.broadcastEvent("showApproveIcon", false);
      }
    }));
  }

  /**
   * Subscribes to reject event
   * @param approvals Approval data
   */
  private subscribeToRejectEvent(approvals: Array<any>) {
    this._subscriptions.push(this._userDashboardService.rejectApprovals(this.approvalComment, this.data, this.data?.projectId).subscribe(response => {
      if (response) {
        this.data.isCompleted = false;
        this.data.dateRejected = Date.now();
        this.closeApprovalMatrixDialog();
      }
    }));
  }

  /**
   * On close approval matrix diaog action
   * */
  private closeApprovalMatrixDialog() {
    this._userDashboardService.displayDataLoader(false);
    this._raciNavigationService.showApprovalMatrixDialog(false);
    this.isApprovalDialogDisplayed = false;
    this.isProjectSelected = false;
    this.approvalComment = '';
  }

  /**
   * Get class for completed task
   * */
  getTaskCompletedClass() {
    if (this.data.dateApproved) {
      return "project-approved";
    }
    else if (!this.data.isCompleted && this.data.dateRejected) {
      return "project-rejected";
    }
    return "";
  }

  /**
   * Event on row select
   * */
  changeSelectedRow(e: SelectionEvent) {
    if (e.selectedRows.length > 0) {
      const item = e.selectedRows[0]?.dataItem;

      this._raciNavigationService.onDisplayProjectDetails(false);
      this._raciNavigationService.onDisplayApprovalHistory(false);
      this._raciNavigationService.onShowProjectDetailsIcon(true);
      this.updateSelectedGeneralTaskProperty.emit(item);
    }
  }

  /**
   * Select all properies
   */
  selectAllProperties() {
    this.isProjectSelected = !this.isProjectSelected;
    if (this.isProjectSelected) {
      this.selectedApprovals = this.properties;
    } else {
      this.selectedApprovals = [];
    }

    this.shouldShowHideApprovalIcon();
  }

  /**
   * Check if approved icon appear
   */
  shouldShowHideApprovalIcon() {
    this._raciNavigationService.onShowApproveIcon(this.selectedApprovals.length > 0);
    if (this.selectedApprovals.length > 0) {
      this.eventBus.broadcastEvent("showApproveIcon", true);
    } else {
      this.eventBus.broadcastEvent("showApproveIcon", false);
    }
  }

  /**
   * Handles show/hide approval dialog
   */
  showRaciApprovalDialog() {
    this._subscriptions.push(this._raciNavigationService._isApprovalDialogDisplayed.subscribe(data => {
      this.isApprovalDialogDisplayed = data;
    }));
  }
}
